import { CallAnalysisData, CallContextInfo } from '../types';

/**
 * Extracts structured context information from call data for AI analysis
 */
export const extractCallContext = (callData: CallAnalysisData | any): CallContextInfo => {
	const { phoneCall, company, contact, creator, interaction, selectedRule } = callData;

	// Extract company vertical from tags
	const companyVertical = company?.tags?.find((tag: string) => tag.includes('Vertical:'))?.replace('Vertical: ', '');

	// Extract company size indicators from tags
	const companySizeTag = company?.tags?.find((tag: string) => tag.includes('SIZE:') || tag.includes('EMPLOYEES:'));

	// Format contact name
	const contactName = contact ? `${contact.firstName} ${contact.lastName}`.trim() : undefined;

	// Format SDR name
	const sdrName = creator ? `${creator.firstName} ${creator.lastName}`.trim() : undefined;

	// Format call date and time
	const callDate = phoneCall.startTime ? new Date(phoneCall.startTime).toISOString().split('T')[0] : '';
	const callTime = phoneCall.startTime ? new Date(phoneCall.startTime).toLocaleTimeString() : '';

	return {
		// Call performance metrics
		duration: phoneCall.duration || 0,
		direction: phoneCall.direction || 'Unknown',
		callStatus: phoneCall.callStatus || 'Unknown',

		// Outcome information
		outcomeName: interaction?.outcome?.name,
		outcomeDescription: interaction?.outcome?.description,

		// Contact context
		contactJobTitle: contact?.jobTitle,
		contactName,

		// Company context
		companyName: company?.companyName,
		companyVertical,
		companySize: companySizeTag,
		timeZone: company?.timeZone,

		// Framework adherence
		selectedRule,

		// Timing context
		callDate,
		callTime,

		// SDR context
		sdrName,
		sdrEmail: creator?.email,
	};
};

/**
 * Formats call context into a readable string for AI analysis
 */
export const formatCallContextForAI = (contextInfo: CallContextInfo): string => {
	const parts: string[] = [];

	// Call metrics
	parts.push(`Call Duration: ${Math.round(contextInfo.duration / 1000)}s`);
	parts.push(`Direction: ${contextInfo.direction}`);
	parts.push(`Status: ${contextInfo.callStatus}`);

	// Outcome
	if (contextInfo.outcomeName) {
		parts.push(`Outcome: ${contextInfo.outcomeName}`);
		if (contextInfo.outcomeDescription) {
			parts.push(`Outcome Details: ${contextInfo.outcomeDescription}`);
		}
	}

	// Contact info
	if (contextInfo.contactName) {
		parts.push(`Contact: ${contextInfo.contactName}`);
	}
	if (contextInfo.contactJobTitle) {
		parts.push(`Job Title: ${contextInfo.contactJobTitle}`);
	}

	// Company info
	if (contextInfo.companyName) {
		parts.push(`Company: ${contextInfo.companyName}`);
	}
	if (contextInfo.companyVertical) {
		parts.push(`Industry: ${contextInfo.companyVertical}`);
	}
	if (contextInfo.timeZone) {
		parts.push(`Time Zone: ${contextInfo.timeZone}`);
	}

	// Framework adherence
	if (contextInfo.selectedRule) {
		parts.push(`Selected Framework Rule: ${contextInfo.selectedRule}`);
	}

	// Timing
	parts.push(`Call Date: ${contextInfo.callDate}`);
	parts.push(`Call Time: ${contextInfo.callTime}`);

	// SDR info
	if (contextInfo.sdrName) {
		parts.push(`SDR: ${contextInfo.sdrName}`);
	}

	return parts.join('\n');
};
